import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { Prismic } from '@landsbankinn/prismic';
import { LayoutContext } from '@landsbankinn/slices/components/contexts/LayoutContext';
import {
  Logo,
  device,
  Language,
  responseFont,
  container,
  sronly,
} from '@landsbankinn/ui';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { animated } from 'react-spring';
import { urlResolver } from 'lib/linkResolver';
import type { NavItem } from './Navigation';
import messages from './navigation.res';

const NAVBAR_HEIGHT = 80;

const Nav = styled.nav<{ isTransparent: boolean; sticky: boolean }>`
  position: relative;
  z-index: 11;
  height: ${NAVBAR_HEIGHT}px;
  display: flex;
  background-color: ${(props) =>
    props.isTransparent ? 'transparent' : props.theme.colors.primary};

  background-image: ${(props) => props.theme.gradients.primary};
`;

const ItemAnchor = styled.a<any>`
  font-family: ${(props) => props.theme.font.family}, arial, sans-serif;
  font-weight: ${(props) => (props.active ? '700' : 'normal')};
  ${responseFont(16, 18)}
  text-decoration: none;
  position: relative;
  height: ${NAVBAR_HEIGHT}px;
  padding-top: 7px;
  align-items: center;
  display: flex;
  margin: 0 20px;
  cursor: pointer;
  overflow: hidden;
  color: white;

  > span {
    opacity: ${(props) => (props.active ? 1 : 0.7)};
    transition: opacity 200ms ease-in-out;
  }

  &:hover > span {
    opacity: 1;
  }

  &:after {
    content: '';
    position: absolute;
    left: calc(50% - 8px);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 8px 8px 8px;
    border-color: transparent transparent #fff transparent;
    bottom: -15px;
    transition: bottom 300ms ease-out;
  }

  ${(props) =>
    props.isOpen &&
    `
    &:after {
      bottom: 0px;
      transition: bottom 300ms ease-in;
    }
  `}

  @media ${device.pageLimit} {
    margin: 0 28px;
    letter-spacing: 0.28px;

    &:first-child {
      margin-left: 20px;
    }
  }
  @media (max-width: 1400px) {
    margin: 0 10px;
    &:first-child {
      margin-left: 10px;
    }
  }
  @media (max-width: 1200px) {
    ${responseFont(12, 15)}
  }
`;

const Item = ({ children, active, ...rest }: any) => {
  return (
    <ItemAnchor
      {...rest}
      active={active}
      onFocus={(e: any) => {
        if (rest.onFocus) {
          rest.onFocus(e);
        }
      }}
    >
      <span>{children}</span>
    </ItemAnchor>
  );
};

const Items = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: 1400px) {
    margin-left: auto;
  }
`;

const Container = styled.div`
  ${container}
  display: flex;
  align-items: center;
`;

const LogoWrapper = styled.a`
  height: ${NAVBAR_HEIGHT}px;
  display: flex;
  align-items: center;
  padding: 0 22px;
  margin-left: -20px;
  cursor: pointer;

  @media (min-width: 1400px) {
    margin-left: -100px;
  }
`;

const SrOnly = styled.span`
  ${sronly}
`;

const Extra = styled(animated.div)<any>`
  height: ${NAVBAR_HEIGHT}px;
  display: flex;
  align-items: center;
`;

const ExtraLink = styled(animated.a)`
  display: flex;
  align-items: center;
  padding: 12px 16px 10px;
  height: ${NAVBAR_HEIGHT}px;
  &:nth-child(2) {
    padding-right: 32px;
  }
`;

const Span = styled.span`
  color: white;
  padding-top: 10px;
  padding-left: 22px;
  width: 289px;
  @media (max-width: 1400px) {
    width: 180px;
  }

  @media (max-width: 1300px) {
    width: 180px;
  }
  @media (max-width: 1200px) {
  }
`;

interface NavBarProps {
  title?: string;
  items: NavItem[];
  active: NavItem | null;
  onClick: any;
  isTransparent: boolean;
  sticky: boolean;
  activeIds: string[];
  lang: string;
}

export function NavBar({
  title,
  items,
  onClick,
  activeIds,
  isTransparent,
  sticky,
  lang,
}: NavBarProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { isNavigationOpen, setIsNavigationOpen } = useContext(LayoutContext);
  const LANG = 'en-gb';
  const router = useRouter();
  const { r } = useResource();
  const [isOpen, setIsOpen] = useState(-1);

  const [lastFocus, setLastFocus] = useState<HTMLAnchorElement | null>(null);
  useEffect(() => {
    if (!isNavigationOpen && lastFocus) {
      lastFocus.focus();
    }
  }, [isNavigationOpen]);

  return (
    <Nav isTransparent={isTransparent} sticky={sticky}>
      <Container>
        <Link href={lang === LANG ? '/en' : '/'}>
          <LogoWrapper
            href={lang === LANG ? '/en' : '/'}
            title={title}
            onClick={() => {
              setIsNavigationOpen(false);
            }}
          >
            <Logo style={{ width: 36, height: 36 }} />
            <SrOnly>{title}</SrOnly>
          </LogoWrapper>
        </Link>
        <Span>
          <Link href={lang === LANG ? '/en' : '/'}>
            {lang === LANG ? 'ANNUAL REPORT 2020' : 'ÁRSSKÝRSLA 2020'}
          </Link>
        </Span>
        <Items ref={ref}>
          {items?.map((item, index) => {
            const link = Prismic.link(
              urlResolver,
              item?.navigation_item?.link,
              item?.navigation_item?.title,
            );
            const isActive = link.as
              ? !!(
                  router.asPath.match(new RegExp(`^${link.as}$`)) ||
                  router.asPath.match(new RegExp(`^${link.as}/`))
                )
              : false;

            const isMega = item.navigation_item.visible !== false;
            const href = isMega ? '#' : !link.as ? link.href : link.as;

            return (
              <Item
                href={href}
                title={link.title}
                active={isActive}
                onFocus={(e: any) => setLastFocus(e.currentTarget)}
                onClick={(e: any) => {
                  e.preventDefault();
                  if (isMega) {
                    onClick(item);
                    if (isOpen === index) {
                      setIsOpen(-1);
                    } else {
                      setIsOpen(index);
                    }
                    return;
                  }
                  setIsOpen(-1);
                  setIsNavigationOpen(false);
                  if (href) {
                    return router.push(href);
                  }
                }}
                key={index}
                isOpen={isOpen === index && !!isNavigationOpen}
              >
                {Prismic.text(item.navigation_title)}
              </Item>
            );
          })}
        </Items>
        <Extra open={isNavigationOpen}>
          <ExtraLink
            href={lang === LANG ? '/' : '/en'}
            title={r(messages.language)}
          >
            <Language />
            <SrOnly>{r(messages.language)}</SrOnly>
          </ExtraLink>
        </Extra>
      </Container>
    </Nav>
  );
}
